import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import Space from "../../general/antd/space";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import { IClient } from "../../../models/client/response";
import DiscountCodeContext from "../../../context/discount-code/context";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import { PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useModal } from "react-modal-hook";
import FormHookModal from "../../general/modals/form-hook-modal";
import GiveClientDiscount from "../discount-code-give-client";
import { IDiscountCodeClient } from "../../../models/discount-code/response";

interface IProps {
    isInvitation: boolean
    userType: number
}

const DiscountCodeClientsTable: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const { actions, loading, query, clientsList } =
        useContext(DiscountCodeContext);
    const { id } = useParams();
    const { direction } = useContext(AppContext);

    useEffect(() => {
        id && actions.getClients(Number(id));
    }, [query, direction, id]);

    const [showFormModal, hideFormodal] = useModal(
        () => (
            <FormHookModal
                id="3"
                confirmButtonText={t("save")}
                loading={loading.includes("create")}
                title={t("give_client_discount")}
                hideModal={() => {
                    hideFormodal();
                }}
                width={"50vw"}
                style={{
                    marginTop: "1.6rem",
                    height: "59vh",
                }}
                onSubmit={async (data) => {
                    await actions.createDiscountCodeClient({
                        clientId: data.clientId,
                        discountCodeId: Number(id),
                    });
                }}
            >
                <GiveClientDiscount></GiveClientDiscount>
            </FormHookModal>
        ),
        [loading]
    );

    const columns: ColumnProps<IDiscountCodeClient>[] = [
        {
            title: t("id"),
            dataIndex: "id",
            key: "id",
            align: "center",
            width: 100,
        },
        {
            title: t("client"),
            dataIndex: "user",
            key: "user",
            align: "center",
            minWidth: 120,
            render: (_: any, record) => {
                return <>{record?.user?.firstName + " " + record?.user?.lastName}</>;
            },
        },
        {
            title: t("mobile"),
            dataIndex: "user",
            key: "user",
            align: "center",
            render: (_: any, record) => {
                return <>{record?.user?.cleanMobile}</>;
            },
        },
        {
            title: t("active_points"),
            dataIndex: "activePoints",
            key: "activePoints",
            align: "center",
            minWidth: 140,
        },
        {
            title: t("expired_points"),
            dataIndex: "expiredPoints",
            key: "expiredPoints",
            align: "center",
            minWidth: 140,
        },
        {
            title: t("created_at"),
            dataIndex: "createdAt",
            key: "createdAt",
            align: "center",
            minWidth: 120,
            render: (createdAt) => {
                return moment(createdAt).format("YYYY-MM-DD");
            },
        },
        {
            title: t("actions"),
            width: 150,
            dataIndex: "",
            align: "center",
            fixed: "right",
            key: "x",
            render: (_: any, record) => (
                <Space>
                    <DeleteBtn
                        onConfirm={async () => {
                            await actions.deleteDiscountCodeClient({
                                clientId: record?.id,
                                discountCodeId: Number(id),
                            });
                            eventManager.emit(EVENT_SUCCESS);
                        }}
                        loading={loading.includes("delete")}
                    />
                </Space>
            ),
        },
    ];

    // Filters && Sorts
    let tableFiltersProps = {};
    const [sorts, setSorts] = useState<ISort[]>([]);
    const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

    useEffect(() => {
        sorts.length > 0 &&
            actions.setQuery({
                ...query,
                orders: sorts,
            });
    }, [sorts]);

    return (
        <>
            <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: " 10px" }}
                onClick={showFormModal}
                disabled={!(!props.isInvitation && props.userType == 3)}
            >
                {t("add_client")}
            </Button>

            <Table<any>
                rowKey="id"
                showPagination={true}
                columns={columns}
                dataSource={clientsList?.data ?? []}
                loading={loading.includes("list")}
                size="small"
                total={clientsList?.total}
                pageSize={query.perPage}
                page={query.page}
                onPaginationChange={(page, pageSize) => {
                    actions.setQuery({
                        ...query,
                        page,
                        perPage: pageSize,
                    });
                }}
                onChange={(_, tableFilters, tabelSorters) => {
                    tableOnChange(
                        tableFilters,
                        tabelSorters,
                        tableFiltersProps,
                        setSorts,
                        setStaticFilters
                    );
                }}
            />
        </>
    );
};

export default DiscountCodeClientsTable;
